import React, { useState, useMemo, Fragment } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Button from '@material-ui/core/Button';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';

import Skeleton from '@material-ui/lab/Skeleton';

import FormatPhoneNumber from 'app/utils/FormatPhoneNumber';
import ProfessionalCategories from 'app/utils/ProfessionalCategories';

import AccountIcon from '@material-ui/icons/AccountCircle';
import CloseIcon from '@material-ui/icons/Close';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogAction: {
        justifyContent: 'center',
    },
    emptyCon: {
        padding: theme.spacing(3),
        textAlign: 'center'
    },
    bigAvatar: {
        width: 100,
        height: 100,
        fontSize: '100px',
        margin: '20px auto'
    },
    section: {
        marginTop: theme.spacing(3)
    },
    sectionCenter: {
        textAlign: 'center'
    },
}));

export default function ViewUserDialog(props) {

    const [ user, setUser ] = useState(null);
    const [ loading, setLoading ] = useState(false);

    const { open, onClose, onClosed, id } = props;

    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();

    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('sm'));

    // Entering Functions ------------------------------------------

    const onEnter = () => {
        if (props.user) {
            setUser(props.user)
        } else {
            getUser();
        }
    }

    // Getter Functions

    const getUser = () => {
        setLoading(true);

        api.get('/user/' + id)
            .then(res => setUser(res.user))
            .finally(() => setLoading(false));
    }

    // Helper Functions

    const hasContact = useMemo(() => {
        if (user && (user.home_email || user.phone_home || user.home_mobile || user.home_fax)) return true;
        return false;
    }, [user]);

    const hasHomeAddress = useMemo(() => {
        if (user && user.address_one && user.city && user.state && user.zip) return true;
        return false;
    }, [user]);

    const hasWork = useMemo(() => {
        if (user && (user.industry || user.employer || user.position )) return true;
        return false;
    }, [user]);

    const hasWorkContact = useMemo(() => {
        if (user && (user.work_email || user.phone_work || user.work_fax )) return true;
        return false;
    }, [user]);

    const hasWorkAddress = useMemo(() => {
        if (user && user.work_address_one && user.work_city && user.work_state && user.work_zip) return true;
        return false;
    }, [user]);

    const hasSpouse = useMemo(() => {
        if (user && (user.marital_status || user.spouse || user.spouse_nickname || user.spouse_email || user.spouse_mobile || user.spouse_company)) return true;
        return false;
    }, [user]);

    // Exting Functions ----------------------------------------

    const onExited = () => {
        setUser(null);
        onClosed();
    }

    return (
        <Dialog
            onEnter={onEnter}
            aria-labelledby="view-user-dialog-title"
            open={open}
            aria-describedby="view-user-dialogue-description"
            fullWidth
            fullScreen={matches}
            maxWidth="sm"
            onExited={onExited}
            >
            <DialogTitle id="view-user-dialog-title">
                {user ? user.first + " " + user.last : ""}
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent id="view-user-dialog-content" dividers>
                {loading ? (
                    <Fragment>
                        <Skeleton variant="circle" height={40} width={40} className={classes.avatarContainer}/>
                        <br/>
                        <Skeleton variant="text" height={10} width="33%" className={classes.avatarContainer}/>
                        <br/>
                        <Skeleton variant="text" height={26} width="50%" className={classes.avatarContainer}/>
                        <br/>
                        <Skeleton variant="rect" height={46} width="100%" className={classes.avatarContainer}/>
                        <br/>
                        <Skeleton variant="text" height={8} width="50%" className={classes.avatarContainer}/>
                    </Fragment>
                ) : user ? (
                    <Fragment>
                        <div className={classes.sectionCenter}>
                            <Avatar
                                src={user.avatar ? user.avatar.full_url : null}
                                className={classes.bigAvatar}
                                alt={user.full_name}
                                >
                                <AccountIcon fontSize='inherit' />
                            </Avatar>
                        </div>
                        <div className={classes.sectionCenter}>
                            <Typography variant="h6" component="h3" id="user-dialogue-title">
                                {user.full_name}
                            </Typography>
                            <Typography variant="subtitle1" component="h4" color="textSecondary">
                                {user.public_membership_title}
                            </Typography>
                        </div>
                        {(user.nickname && user.nickname != "") &&
                            <div className={classes.section}>
                                <Grid container spacing={1} justifyContent="space-between">
                                    <Grid item>
                                        <Typography variant="body1" component="h4">
                                            Nickname
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body1" component="h4" color='textSecondary'>
                                            {user.nickname}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </div>
                        }
                        <div className={classes.section}>
                            <Typography variant="body1" component="h4" gutterBottom>
                                Contact Information
                            </Typography>
                            {hasContact ? (
                                <Typography variant="subtitle2" component="div" color="textSecondary">
                                    { (user.home_email && user.home_email != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Email:</Grid>
                                            <Grid item>
                                                <strong>
                                                    <Link
                                                        color="secondary"
                                                        href={'mailto:' + user.home_email}
                                                        >
                                                        {user.home_email}
                                                    </Link>
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.phone_home && user.phone_home != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Home Phone:</Grid>
                                            <Grid item>
                                                <strong>
                                                    <Link
                                                        color="secondary"
                                                        href={'tel:' + user.phone_home}
                                                        >
                                                        {FormatPhoneNumber(user.phone_home)}
                                                    </Link>
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.home_mobile && user.home_mobile != "") &&
                                        <Grid container spacing={3} justify="space-between">
                                            <Grid item>Mobile Phone:</Grid>
                                            <Grid item>
                                                <strong>
                                                    <Link
                                                        color="secondary"
                                                        href={'tel:' + user.home_mobile}
                                                        >
                                                        {FormatPhoneNumber(user.home_mobile)}
                                                    </Link>
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.home_fax && user.home_fax != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Home Fax:</Grid>
                                            <Grid item>
                                                <strong>
                                                    {FormatPhoneNumber(user.home_fax)}
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                </Typography>
                            ) : (
                                <Typography variant="subtitle2" color="textSecondary">
                                    No contact information available.
                                </Typography>
                            )}
                        </div>
                        <div className={classes.section}>
                            <Typography variant="body1" component="h4" gutterBottom>
                                Home Address
                            </Typography>
                            {hasHomeAddress ? (
                                <Typography variant="subtitle2" component="div" color="textSecondary">
                                    <Grid container spacing={1}>
                                        <Grid xs={12} item>
                                            {user.address_one}&nbsp;&nbsp;&nbsp;
                                            {user.address_two ? user.address_two : ""}<br/>
                                            {user.city}&nbsp;&nbsp;&nbsp;
                                            {user.state}<br/>
                                            {user.zip}
                                        </Grid>
                                    </Grid>
                                </Typography>
                            ) : (
                                <Typography variant="subtitle2" color="textSecondary">
                                    No contact information available.
                                </Typography>
                            )}
                        </div>
                        <div className={classes.section}>
                            <Typography variant="body1" component="h4" gutterBottom>
                                Work Information
                            </Typography>
                            {hasWork ? (
                                <Typography variant="subtitle2" component="div" color="textSecondary">
                                    { (user.industry && user.industry != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Industry:</Grid>
                                            <Grid item>
                                                <strong>
                                                    {ProfessionalCategories.find(pc => pc.value == user.industry).name}
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.employer && user.employer != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Employer:</Grid>
                                            <Grid item><strong>{user.employer}</strong></Grid>
                                        </Grid>
                                    }
                                    { (user.position && user.position != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Position:</Grid>
                                            <Grid item><strong>{user.position}</strong></Grid>
                                        </Grid>
                                    }
                                </Typography>
                            ) : (
                                <Typography variant="subtitle2" color="textSecondary">
                                    No work information available.
                                </Typography>
                            )}
                        </div>
                        <div className={classes.section}>
                            <Typography variant="body1" component="h4" gutterBottom>
                                Work Contact
                            </Typography>
                            {hasWorkContact ? (
                                <Typography variant="subtitle2" component="div" color="textSecondary">
                                    { (user.work_email && user.work_email != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Work Email:</Grid>
                                            <Grid item>
                                                <strong>
                                                    <Link
                                                        color="secondary"
                                                        href={'mailto:' + user.work_email}
                                                        >
                                                        {user.work_email}
                                                    </Link>
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.phone_work && user.phone_work != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Work Phone:</Grid>
                                            <Grid item>
                                                <strong>
                                                    <Link
                                                        color="secondary"
                                                        href={'tel:' + user.phone_work}
                                                        >
                                                        {FormatPhoneNumber(user.phone_work)}
                                                    </Link>
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.work_fax && user.work_fax != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Work Fax:</Grid>
                                            <Grid item>
                                                <strong>
                                                    {FormatPhoneNumber(user.work_fax)}
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                </Typography>
                            ) : (
                                <Typography variant="subtitle2" color="textSecondary">
                                    No work contact information available.
                                </Typography>
                            )}
                        </div>
                        <div className={classes.section}>
                            <Typography variant="body1" component="h4" gutterBottom>
                                Work Address
                            </Typography>
                            {hasWorkAddress ? (
                                <Typography variant="subtitle2" component="div" color="textSecondary">
                                    <Grid container spacing={1}>
                                        <Grid xs={12} item>
                                            {user.work_address_one}&nbsp;&nbsp;&nbsp;
                                            {user.work_address_two ? user.work_address_two : ""}<br/>
                                            {user.work_city}&nbsp;&nbsp;&nbsp;
                                            {user.work_state}<br/>
                                            {user.work_zip}
                                        </Grid>
                                    </Grid>
                                </Typography>
                            ) : (
                                <Typography variant="subtitle2" color="textSecondary">
                                    No work address information available.
                                </Typography>
                            )}
                        </div>
                        <div className={classes.section}>
                            <Typography variant="body1" component="h4" gutterBottom>
                                Spouse Information
                            </Typography>
                            {hasSpouse ? (
                                <Typography variant="subtitle2" component="div" color="textSecondary">
                                    { (user.marital_status && user.marital_status != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Marital Status:</Grid>
                                            <Grid item>
                                                <strong>
                                                    {user.marital_status}
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.spouse && user.spouse != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Spouse:</Grid>
                                            <Grid item>
                                                <strong>
                                                    {user.spouse}
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.spouse_nickname && user.spouse_nickname != "") &&
                                        <Grid container spacing={3} justify="space-between">
                                            <Grid item>Spouse Nickname:</Grid>
                                            <Grid item>
                                                <strong>
                                                    {user.spouse_nickname}
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.spouse_email && user.spouse_email != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Spouse Email:</Grid>
                                            <Grid item>
                                                <strong>
                                                    <Link
                                                        color="secondary"
                                                        href={'mailto:' + user.spouse_email}
                                                        >
                                                        {user.spouse_email}
                                                    </Link>
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.spouse_mobile && user.spouse_mobile != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Spouse Mobile:</Grid>
                                            <Grid item>
                                                <strong>
                                                    {FormatPhoneNumber(user.spouse_mobile)}
                                                </strong>
                                            </Grid>
                                        </Grid>
                                    }
                                    { (user.spouse_company && user.spouse_company != "") &&
                                        <Grid container spacing={1} justify="space-between">
                                            <Grid item>Employer:</Grid>
                                            <Grid item><strong>{user.spouse_company}</strong></Grid>
                                        </Grid>
                                    }
                                </Typography>
                            ) : (
                                <Typography variant="subtitle2" color="textSecondary">
                                    No spouse information available.
                                </Typography>
                            )}
                        </div>
                    </Fragment>
                ) : (
                    <Typography
                        component="h3"
                        color="textSecondary"
                        variant="h6"
                        className={classes.emptyCon}
                        >
                        No matching user found.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions classes={{ root: classes.dialogAction }}>
                <Button onClick={onClose} color="primary" variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
